import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtError } from "../api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Mail, Loader2, Users, Check, CheckSquare, Square, Plus, MapPin, Trash2, ChevronDown, ChevronRight } from "lucide-react";

const ROLES = [
  { key: "janitor", label: "Hausmeister" },
  { key: "manager", label: "Manager" },
  { key: "admin", label: "Administrator" },
];
const LEGACY = { hausmeister: "Hausmeister (alt)", management: "Management (alt)", melder: "Melder (alt)" };
const roleLabel = (g) => {
  const k = (g || "").toLowerCase();
  return ROLES.find((r) => r.key === k)?.label || LEGACY[k] || g || "—";
};
const initials = (t) => {
  const s = (t || "?").replace(/@.*/, "");
  const parts = s.split(/[.\-_ ]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : s.slice(0, 2)).toUpperCase() || "?";
};

// Eine Nutzerzeile: Klick klappt die Standorte auf. Dort:
//  - Gruppe fuer ALLE Objekte des Nutzers aendern (alle-oder-keine),
//  - einzelne Standorte entfernen,
//  - den Nutzer zu weiteren Standorte hinzufuegen.
function UserRow({ user, manageObjects, token, onChanged }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const owned = useMemo(() => new Set(user.objects.map((o) => o.permid)), [user]);
  const available = useMemo(() => manageObjects.filter((o) => !owned.has(o.permid)), [manageObjects, owned]);

  const uniformGroup = user.groups.length === 1 ? String(user.groups[0]).toLowerCase() : "";
  const [groupSel, setGroupSel] = useState(uniformGroup || "janitor");
  const [addSel, setAddSel] = useState(() => new Set());
  const [confirmAll, setConfirmAll] = useState(false);

  const groupOptions = uniformGroup ? ROLES : [{ key: "", label: "— gemischt —" }, ...ROLES];

  const changeGroupAll = async (newRole) => {
    if (!newRole) return;
    setGroupSel(newRole);
    setBusy("group");
    try {
      for (const o of user.objects) {
        await api.post(`/magauth/permissions/${o.permid}/change`, { email: user.user, role: newRole }, auth);
      }
      toast.success(`Gruppe von ${user.user} auf ${roleLabel(newRole)} geändert (${user.objects.length} Objekt(e)).`);
      await onChanged();
    } catch (e) {
      toast.error(fmtError(e, "Gruppe konnte nicht geändert werden"));
      await onChanged();
    } finally { setBusy(null); }
  };

  const removeOne = async (permid, title) => {
    setBusy(permid);
    try {
      await api.post(`/magauth/permissions/${permid}/revoke`, { email: user.user }, auth);
      toast.success(`${user.user} aus „${title || permid}" entfernt.`);
      await onChanged();
    } catch (e) {
      toast.error(fmtError(e, "Nutzer konnte nicht entfernt werden"));
    } finally { setBusy(null); }
  };

  const toggleAdd = (pid) => setAddSel((s) => { const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });

  const addSelected = async () => {
    const permids = [...addSel];
    if (permids.length === 0) return;
    const role = groupSel || uniformGroup || "janitor";
    setBusy("add");
    try {
      const { data } = await api.post("/magauth/permissions/bulk-grant", { email: user.user, role, permids }, auth);
      if (data.granted > 0) toast.success(`${user.user} auf ${data.granted} weitere Standort(en) berechtigt.`);
      else toast.error("Hinzufügen fehlgeschlagen.");
      setAddSel(new Set());
      await onChanged();
    } catch (e) {
      toast.error(fmtError(e, "Hinzufügen fehlgeschlagen"));
    } finally { setBusy(null); }
  };

  const removeAll = async () => {
    setBusy("all");
    let ok = 0;
    for (const o of user.objects) {
      try { await api.post(`/magauth/permissions/${o.permid}/revoke`, { email: user.user }, auth); ok++; } catch { /* einzelne Fehler ignorieren */ }
    }
    toast.success(`${user.user} aus ${ok}/${user.objects.length} Standort(en) entfernt.`);
    setConfirmAll(false);
    await onChanged();
    setBusy(null);
  };

  return (
    <div className="card-wd p-0 overflow-hidden" data-testid={`nutzer-user-row-${user.user}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`nutzer-user-toggle-${user.user}`}
        className="w-full flex items-center gap-3 p-3 sm:p-4 text-left" style={{ background: "none", border: "none", cursor: "pointer" }}>
        <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm" style={{ background: "#FFEAD6", color: "#C2410C" }}>
          {initials(user.user)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate" data-testid={`nutzer-email-${user.user}`}>{user.user}</div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {user.groups.length ? user.groups.map((g) => (
              <span key={g} className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#EFF6FF", color: "#1D4ED8" }}>{roleLabel(g)}</span>
            )) : <span className="text-xs" style={{ color: "#94A3B8" }}>ohne Gruppe</span>}
          </div>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-md flex-shrink-0" style={{ background: "#F1F5F9", color: "#64748B" }}>{user.count} Objekt(e)</span>
        {open ? <ChevronDown size={18} color="#94A3B8" /> : <ChevronRight size={18} color="#94A3B8" />}
      </button>

      {open && (
        <div className="px-3 sm:px-4 pb-4" data-testid={`nutzer-expanded-${user.user}`}>
          {/* Gruppe fuer ALLE Objekte */}
          <div className="flex items-center gap-2 flex-wrap p-3 rounded-xl mb-3" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
            <span className="text-xs font-bold" style={{ color: "#475569" }}>Gruppe (für alle Standorte):</span>
            <select data-testid={`nutzer-group-all-${user.user}`} className="input-wd" disabled={busy === "group"}
              value={groupSel} onChange={(e) => changeGroupAll(e.target.value)} style={{ width: "auto", padding: "5px 8px", fontSize: 13 }}>
              {groupOptions.map((r) => <option key={r.key} value={r.key} disabled={r.key === ""}>{r.label}</option>)}
            </select>
            {busy === "group" && <Loader2 size={15} className="animate-spin" color="#F97316" />}
          </div>

          {/* Standorte einzeln entfernen */}
          <div className="text-xs font-bold mb-1" style={{ color: "#475569" }}>Berechtigte Standorte</div>
          <div className="flex flex-col gap-1.5">
            {user.objects.map((o) => (
              <div key={o.permid} data-testid={`nutzer-entry-${user.user}-${o.permid}`} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "#fff", border: "1px solid #E2E8F0" }}>
                <MapPin size={13} color="#F97316" className="flex-shrink-0" />
                <span className="text-sm min-w-0 flex-1 truncate">{o.title}</span>
                {o.group && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#64748B" }}>{roleLabel(o.group)}</span>}
                <button data-testid={`nutzer-remove-${user.user}-${o.permid}`} onClick={() => removeOne(o.permid, o.title)} disabled={busy === o.permid}
                  title="Von dieser Standort entfernen" className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "#FEF2F2", border: "none", cursor: "pointer" }}>
                  {busy === o.permid ? <Loader2 size={14} className="animate-spin" color="#B91C1C" /> : <Trash2 size={14} color="#B91C1C" />}
                </button>
              </div>
            ))}
          </div>

          {/* Weitere Standorte hinzufuegen */}
          {available.length > 0 ? (
            <div className="mt-3">
              <div className="text-xs font-bold mb-1" style={{ color: "#475569" }}>Weitere Standorte hinzufügen</div>
              <div className="flex flex-col gap-1.5" style={{ maxHeight: 220, overflowY: "auto" }} data-testid={`nutzer-add-list-${user.user}`}>
                {available.map((o) => {
                  const on = addSel.has(o.permid);
                  return (
                    <button key={o.permid} data-testid={`nutzer-add-target-${user.user}-${o.permid}`} onClick={() => toggleAdd(o.permid)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-left"
                      style={{ border: `1px solid ${on ? "#F97316" : "#E2E8F0"}`, background: on ? "#FFF7ED" : "#fff", cursor: "pointer" }}>
                      <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: on ? "#F97316" : "#F1F5F9" }}>
                        {on && <Check size={13} color="#fff" />}
                      </span>
                      <span className="text-sm min-w-0 flex-1 truncate">{o.title}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-2">
                <button data-testid={`nutzer-add-liegenschaft-${user.user}`} className="btn-primary" disabled={busy === "add" || addSel.size === 0} onClick={addSelected} style={{ padding: "8px 14px", fontSize: 13 }}>
                  {busy === "add" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {addSel.size > 0 ? `${addSel.size} hinzufügen` : "Hinzufügen"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs mt-3 m-0" style={{ color: "#94A3B8" }}>Auf allen von dir verwaltbaren Standorte berechtigt.</p>
          )}

          <div className="mt-4 pt-3" style={{ borderTop: "1px solid #E2E8F0" }}>
            {!confirmAll ? (
              <button data-testid={`nutzer-remove-all-${user.user}`} onClick={() => setConfirmAll(true)} disabled={busy === "all"}
                className="text-sm font-bold inline-flex items-center gap-2" style={{ background: "none", border: "none", cursor: "pointer", color: "#B91C1C" }}>
                <Trash2 size={15} /> Person aus allen Rechten entfernen
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold" style={{ color: "#B91C1C" }}>{user.user} aus allen {user.objects.length} Standort(en) entfernen?</span>
                <button data-testid={`nutzer-remove-all-confirm-${user.user}`} onClick={removeAll} disabled={busy === "all"} className="btn-primary" style={{ background: "#DC2626", padding: "6px 12px", fontSize: 13 }}>
                  {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : "Ja, alle entfernen"}
                </button>
                <button onClick={() => setConfirmAll(false)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }}>Abbrechen</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Nutzerverwaltung: 1) Uebersicht/Akkordeon aller Nutzer, 2) neuen Nutzer hinzufuegen (Bulk-Grant).
export function NutzerVerwaltung({ onClose }) {
  const token = sessionStorage.getItem("mag_token");
  const [screen, setScreen] = useState("list"); // 'list' | 'add'

  const [wd, setWd] = useState(null);
  const [wdError, setWdError] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/magauth/wdobjects", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setWd(r.data); })
      .catch((e) => { if (alive) setWdError(fmtError(e, "Objekte konnten nicht geladen werden")); });
    return () => { alive = false; };
  }, [token]);
  const liegenschaften = useMemo(() => wd?.liegenschaften || [], [wd]);
  const parents = useMemo(() => wd?.parents || [], [wd]);

  // Alle sichtbaren Objekte an die Uebersicht geben; die Berechtigung (Admin/Owner)
  // prueft das Backend autoritativ je Objekt. So werden auch Objekte beruecksichtigt,
  // auf denen man Admin, aber nicht Owner ist (MWS zeigt advanced_permissions sonst nur Ownern).
  const manageObjects = useMemo(() => {
    const map = new Map();
    liegenschaften.filter((l) => l.perm_id && !l.error).forEach((l) => map.set(l.perm_id, { permid: l.perm_id, title: l.title || "" }));
    parents.filter((p) => p.perm_id).forEach((p) => { if (!map.has(p.perm_id)) map.set(p.perm_id, { permid: p.perm_id, title: p.title || "" }); });
    return [...map.values()];
  }, [liegenschaften, parents]);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOverview = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/magauth/permissions/overview",
        { objects: manageObjects.map((o) => ({ permid: o.permid, title: o.title || "" })) },
        { headers: { Authorization: `Bearer ${token}` } });
      setOverview(data);
    } catch (e) {
      setError(fmtError(e, "Nutzerübersicht konnte nicht geladen werden"));
    } finally { setLoading(false); }
  }, [manageObjects, token]);

  useEffect(() => { if (wd) loadOverview(); }, [wd, loadOverview]);

  // --- Neuen Nutzer hinzufuegen (Bulk-Grant) ---
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("janitor");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const isAdmin = role === "admin";
  // Alle sichtbaren Objekte als Auswahl anbieten; ob der Eingeloggte dort vergeben darf,
  // prueft das Backend (bulk-grant) autoritativ je Objekt. So erscheinen auch Standorte,
  // auf denen man Admin, aber nicht Owner ist (MWS liefert can_manage sonst nur Ownern).
  const liegTargets = useMemo(() => liegenschaften.filter((l) => l.perm_id && !l.error), [liegenschaften]);
  const parentTargets = useMemo(() => parents.filter((p) => p.perm_id), [parents]);
  const targets = isAdmin ? parentTargets : liegTargets;
  const titleFor = (pid) => (targets.find((t) => t.perm_id === pid)?.title) || pid;

  useEffect(() => { setSelected(new Set()); setResults(null); }, [role]);

  const toggle = (pid) => setSelected((s) => { const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });
  const allSelected = targets.length > 0 && targets.every((t) => selected.has(t.perm_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(targets.map((t) => t.perm_id)));

  const openAdd = () => { setEmail(""); setRole("janitor"); setSelected(new Set()); setResults(null); setScreen("add"); };

  const submit = async () => {
    const em = email.trim();
    if (!em.includes("@")) { toast.error("Bitte eine gültige E-Mail-Adresse eingeben."); return; }
    const permids = [...selected];
    if (permids.length === 0) {
      toast.error(isAdmin ? "Bitte mindestens ein übergeordnetes Objekt auswählen." : "Bitte mindestens eine Standort auswählen.");
      return;
    }
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post("/magauth/permissions/bulk-grant", { email: em, role, permids }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(data);
      if (data.granted > 0) {
        toast.success(`${em} als ${roleLabel(role)} auf ${data.granted}/${data.total} Objekt(en) berechtigt.`);
        await loadOverview();
        setScreen("list");
      } else {
        toast.error("Berechtigung konnte nicht vergeben werden.");
      }
    } catch (e) {
      toast.error(fmtError(e, "Berechtigung konnte nicht vergeben werden"));
    } finally { setBusy(false); }
  };

  const users = overview?.users || [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "#F8FAFC" }} data-testid="nutzer-verwaltung">
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: "#fff", borderBottom: "1px solid #E2E8F0" }}>
        <button data-testid="nutzer-back" onClick={screen === "add" ? () => setScreen("list") : onClose}
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "#F1F5F9", border: "none", cursor: "pointer" }}><ArrowLeft size={18} /></button>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF7ED" }}><Users size={18} color="#F97316" /></span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-base truncate">Nutzer verwalten</div>
          <div className="text-xs truncate" style={{ color: "#64748B" }}>
            {screen === "add" ? "Neuen Nutzer berechtigen" : "Auf einen Nutzer tippen, um Rechte zu bearbeiten"}
          </div>
        </div>
        {screen === "list" && (
          <button data-testid="nutzer-add-open" onClick={openAdd} className="btn-primary flex-shrink-0" style={{ padding: "9px 14px" }}>
            <Plus size={16} /> <span className="hidden sm:inline">Neuer Nutzer</span>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="container-wd py-6" style={{ maxWidth: 820 }}>
          {screen === "list" ? (
            <div data-testid="nutzer-overview">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-extrabold uppercase m-0" style={{ color: "#64748B", letterSpacing: ".04em" }} data-testid="nutzer-list-title">
                  Nutzer{overview ? ` (${users.length})` : ""}
                </h3>
                {overview && <span className="text-xs" style={{ color: "#94A3B8" }}>aus {overview.object_count} Objekt(en)</span>}
              </div>

              {(error || wdError) && <div className="card-wd p-4 mb-3" style={{ borderColor: "#DC2626" }} data-testid="nutzer-error"><p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error || wdError}</p></div>}
              {loading && <p className="text-sm" style={{ color: "#64748B" }} data-testid="nutzer-overview-loading">Nutzer werden geladen…</p>}
              {!loading && users.length === 0 && !error && (
                <div className="card-wd p-8 text-center" data-testid="nutzer-overview-empty">
                  <Users size={28} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
                  <p className="text-sm m-0" style={{ color: "#64748B" }}>Noch keine Nutzer mit Rechten. Füge über „Neuer Nutzer" den ersten hinzu.</p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {users.map((u) => (
                  <UserRow key={u.user} user={u} manageObjects={manageObjects} token={token} onChanged={loadOverview} />
                ))}
              </div>

              {overview?.errors?.length > 0 && (
                <div className="card-wd p-4 mt-4" style={{ borderColor: "#F59E0B" }} data-testid="nutzer-overview-errors">
                  <p className="text-sm font-bold m-0 mb-1" style={{ color: "#B45309" }}>Für {overview.errors.length} Objekt(e) konnten die Rechte nicht geladen werden.</p>
                  <ul className="text-xs m-0 pl-5" style={{ color: "#92400E", listStyle: "disc" }}>
                    {overview.errors.map((er) => <li key={er.permid}>{er.title}: {er.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="card-wd p-4 sm:p-5" data-testid="nutzer-add-view">
              <h3 className="text-sm font-extrabold uppercase mb-3" style={{ color: "#64748B", letterSpacing: ".04em" }}>Neuen Nutzer berechtigen</h3>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1">
                  <label className="text-xs font-bold" style={{ color: "#475569" }}>E-Mail-Adresse</label>
                  <div style={{ position: "relative" }} className="mt-1">
                    <Mail size={15} style={{ position: "absolute", left: 10, top: 12, color: "#94A3B8" }} />
                    <input data-testid="nutzer-add-email" type="email" className="input-wd" style={{ paddingLeft: 32 }}
                      placeholder="name@beispiel.de" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                </div>
                <div className="sm:w-48">
                  <label className="text-xs font-bold" style={{ color: "#475569" }}>Rolle</label>
                  <select data-testid="nutzer-add-role" className="input-wd mt-1" value={role} onChange={(e) => setRole(e.target.value)}>
                    {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold" style={{ color: "#475569" }}>
                    {isAdmin ? "Übergeordnete Objekte auswählen (Parent)" : "Standorte auswählen"}
                  </label>
                  {targets.length > 0 && (
                    <button data-testid="nutzer-select-all" onClick={toggleAll} className="text-xs font-semibold inline-flex items-center gap-1"
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#F97316" }}>
                      {allSelected ? <CheckSquare size={14} /> : <Square size={14} />} {allSelected ? "Alle abwählen" : "Alle auswählen"}
                    </button>
                  )}
                </div>
                {isAdmin && (
                  <p className="text-xs mb-2" style={{ color: "#64748B" }} data-testid="nutzer-parent-hint">
                    Rechte auf dem übergeordneten Objekt gelten für alle vorhandenen und neuen Standorte darunter.
                  </p>
                )}
                {targets.length === 0 ? (
                  <div className="card-wd p-4 text-center" data-testid="nutzer-no-targets">
                    <p className="text-sm m-0" style={{ color: "#64748B" }}>
                      {isAdmin
                        ? "Keine übergeordneten Objekte, auf denen du Rechte vergeben darfst."
                        : "Keine Standorte, auf denen du Rechte vergeben darfst."}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5" style={{ maxHeight: 320, overflowY: "auto" }} data-testid="nutzer-target-list">
                    {targets.map((t) => {
                      const on = selected.has(t.perm_id);
                      return (
                        <button key={t.perm_id} data-testid={`nutzer-target-${t.perm_id}`} onClick={() => toggle(t.perm_id)}
                          className="flex items-center gap-3 px-3 py-2 rounded-xl text-left"
                          style={{ border: `1px solid ${on ? "#F97316" : "#E2E8F0"}`, background: on ? "#FFF7ED" : "#fff", cursor: "pointer" }}>
                          <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: on ? "#F97316" : "#F1F5F9" }}>
                            {on && <Check size={14} color="#fff" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-bold text-sm truncate">{t.title || t.perm_id}</span>
                            {t.parent_title && !isAdmin && <span className="block text-xs truncate" style={{ color: "#94A3B8" }}>{t.parent_title}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end mt-4">
                <button data-testid="nutzer-add-submit" className="btn-primary" disabled={busy || targets.length === 0} onClick={submit} style={{ height: 44, whiteSpace: "nowrap" }}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Berechtigung vergeben
                </button>
              </div>

              {results && (
                <div className="mt-5 pt-4" style={{ borderTop: "1px solid #E2E8F0" }} data-testid="nutzer-results">
                  <h4 className="text-sm font-extrabold uppercase mb-3" style={{ color: "#64748B", letterSpacing: ".04em" }}>
                    Ergebnis ({results.granted}/{results.total} erfolgreich)
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {results.results.map((r) => (
                      <div key={r.permid} data-testid={`nutzer-result-${r.permid}`} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg"
                        style={{ background: r.ok ? "#F0FDF4" : "#FEF2F2" }}>
                        <span className="font-bold flex-1 min-w-0 truncate" style={{ color: "#0F172A" }}>{titleFor(r.permid)}</span>
                        {r.ok
                          ? <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "#15803D" }}><Check size={13} /> berechtigt</span>
                          : <span className="text-xs font-bold" style={{ color: "#B91C1C" }}>{r.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
